/**
 * Sistema de Cache Centralizado e Inteligente
 * Substitui todos os caches manuais por um sistema unificado
 */

export interface CacheEntry<T = any> {
  data: T
  timestamp: number
  ttl: number
  tags: string[]
  hits: number
  lastAccess: number
}

export interface CacheConfig {
  maxSize?: number
  defaultTTL?: number
  enableDebug?: boolean
  enableStats?: boolean
}

export interface CacheStats {
  totalEntries: number
  totalHits: number
  totalMisses: number
  hitRate: number
  memoryUsage: string
  oldestEntry?: string
  newestEntry?: string
}

export class CacheService {
  private static instance: CacheService
  private cache = new Map<string, CacheEntry>()
  private config: Required<CacheConfig>
  private stats = {
    hits: 0,
    misses: 0,
    sets: 0,
    deletes: 0
  }

  constructor(config: CacheConfig = {}) {
    this.config = {
      maxSize: config.maxSize ?? 200,
      defaultTTL: config.defaultTTL ?? 5 * 60 * 1000,
      enableDebug: config.enableDebug ?? false,
      enableStats: config.enableStats ?? true
    }

    // Auto-limpeza a cada 2 minutos
    if (typeof window !== 'undefined') {
      setInterval(() => this.cleanup(), 2 * 60 * 1000)
    }
  }

  static getInstance(config?: CacheConfig): CacheService {
    if (!CacheService.instance) {
      CacheService.instance = new CacheService(config)
    }
    return CacheService.instance
  }

  /**
   * Armazena um item no cache
   */
  set<T>(
    key: string, 
    data: T, 
    options: {
      ttl?: number
      tags?: string[]
    } = {}
  ): void {
    const now = Date.now()
    const ttl = options.ttl ?? this.config.defaultTTL
    
    // Verificar limite de tamanho
    if (this.cache.size >= this.config.maxSize) {
      this.evictLRU()
    }

    const entry: CacheEntry<T> = {
      data,
      timestamp: now,
      ttl,
      tags: options.tags ?? [],
      hits: 0,
      lastAccess: now
    }

    this.cache.set(key, entry)
    this.stats.sets++

    this.debug(`SET: ${key}`, { ttl, tags: options.tags })
  }

  /**
   * Recupera um item do cache
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key) as CacheEntry<T> | undefined
    
    if (!entry) {
      this.stats.misses++
      this.debug(`MISS: ${key}`)
      return null
    }

    const now = Date.now()
    
    // Verificar se expirou
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(key)
      this.stats.misses++
      this.debug(`EXPIRED: ${key}`)
      return null
    }

    // Atualizar estatísticas de acesso
    entry.hits++
    entry.lastAccess = now
    this.stats.hits++

    this.debug(`HIT: ${key}`, { hits: entry.hits })
    return entry.data
  }

  /**
   * Verifica se uma chave existe e não expirou
   */
  has(key: string): boolean {
    return this.get(key) !== null
  }

  /**
   * Remove um item específico
   */
  delete(key: string): boolean {
    const deleted = this.cache.delete(key)
    if (deleted) {
      this.stats.deletes++
      this.debug(`DELETE: ${key}`)
    }
    return deleted
  }

  /**
   * Remove itens por tags
   */
  invalidateByTags(tags: string[]): number {
    let deletedCount = 0
    
    for (const [key, entry] of this.cache.entries()) {
      if (entry.tags.some(tag => tags.includes(tag))) {
        this.cache.delete(key)
        deletedCount++
        this.debug(`INVALIDATE: ${key}`, { tags })
      }
    }

    this.stats.deletes += deletedCount
    return deletedCount
  }

  /**
   * Remove itens por padrão de chave
   */
  invalidateByPattern(pattern: string | RegExp): number {
    let deletedCount = 0
    const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern
    
    for (const key of this.cache.keys()) {
      if (regex.test(key)) {
        this.cache.delete(key)
        deletedCount++
        this.debug(`INVALIDATE_PATTERN: ${key}`, { pattern: pattern.toString() })
      }
    }

    this.stats.deletes += deletedCount
    return deletedCount
  }

  /**
   * Limpa todo o cache
   */
  clear(): void {
    const size = this.cache.size
    this.cache.clear()
    this.stats.deletes += size
    this.debug(`CLEAR: ${size} entries`)
  }

  /**
   * Limpeza automática de itens expirados
   */
  cleanup(): number {
    const now = Date.now()
    let cleanedCount = 0

    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        this.cache.delete(key)
        cleanedCount++
      }
    }

    if (cleanedCount > 0) {
      this.debug(`CLEANUP: ${cleanedCount} expired entries`)
    }

    return cleanedCount
  }

  /**
   * Remove o item menos recentemente usado (LRU)
   */
  private evictLRU(): void {
    let oldestKey = ''
    let oldestAccess = Date.now()

    for (const [key, entry] of this.cache.entries()) {
      if (entry.lastAccess < oldestAccess) {
        oldestAccess = entry.lastAccess
        oldestKey = key
      }
    }

    if (oldestKey) {
      this.cache.delete(oldestKey)
      this.debug(`EVICT_LRU: ${oldestKey}`)
    }
  }

  /**
   * Obtém estatísticas do cache
   */
  getStats(): CacheStats {
    const totalRequests = this.stats.hits + this.stats.misses
    const hitRate = totalRequests > 0 ? (this.stats.hits / totalRequests) * 100 : 0

    // Calcular uso aproximado de memória
    const memoryUsage = this.estimateMemoryUsage()

    // Encontrar entradas mais antigas e mais novas
    let oldestEntry = ''
    let newestEntry = ''
    let oldestTime = Date.now()
    let newestTime = 0

    for (const [key, entry] of this.cache.entries()) {
      if (entry.timestamp < oldestTime) {
        oldestTime = entry.timestamp
        oldestEntry = key
      }
      if (entry.timestamp > newestTime) {
        newestTime = entry.timestamp
        newestEntry = key
      }
    }

    return {
      totalEntries: this.cache.size,
      totalHits: this.stats.hits,
      totalMisses: this.stats.misses,
      hitRate: Math.round(hitRate * 100) / 100,
      memoryUsage,
      oldestEntry: oldestEntry || undefined,
      newestEntry: newestEntry || undefined
    }
  }

  /**
   * Estima o uso de memória do cache
   */
  private estimateMemoryUsage(): string {
    let totalSize = 0
    
    for (const [key, entry] of this.cache.entries()) {
      // Estimativa aproximada do tamanho
      totalSize += key.length * 2 // UTF-16
      totalSize += JSON.stringify(entry.data).length * 2
      totalSize += 200 // Overhead da estrutura CacheEntry
    }

    if (totalSize < 1024) {
      return `${totalSize} B`
    } else if (totalSize < 1024 * 1024) {
      return `${Math.round(totalSize / 1024)} KB`
    } else {
      return `${Math.round(totalSize / (1024 * 1024))} MB`
    }
  }

  /**
   * Lista todas as chaves do cache (para debug)
   */
  keys(): string[] {
    return Array.from(this.cache.keys())
  }

  inspect(key: string): CacheEntry | null {
    return this.cache.get(key) ?? null
  }

  private debug(message: string, data?: any): void {
    if (this.config.enableDebug) {
      console.log(`[Cache] ${message}`, data || '')
    }
  }
}

// Instância global
export const cacheService = CacheService.getInstance()

// Configurações pré-definidas por contexto
export const CachePresets = {
  // Cache de curta duração para APIs dinâmicas
  SHORT: { ttl: 30 * 1000 }, // 30 segundos
  
  // Cache padrão para dados de usuário
  MEDIUM: { ttl: 5 * 60 * 1000 }, // 5 minutos
  
  // Cache longo para dados estáticos
  LONG: { ttl: 30 * 60 * 1000 }, // 30 minutos
  
  // Cache muito longo para recursos estáticos
  STATIC: { ttl: 24 * 60 * 60 * 1000 }, // 24 horas

  // Tags para invalidação inteligente
  TAGS: {
    USER: 'user',
    PROJECT: 'project',
    FILE_TREE: 'file-tree',
    AUTH: 'auth',
    ADMIN: 'admin',
    PDF: 'pdf'
  }
} as const

// Utilitários para chaves de cache
export const CacheKeys = {
  // Gerar chave para projetos do usuário
  userProjects: (userId: number) => `projects:user:${userId}`,
  
  // Gerar chave para árvore de arquivos
  fileTree: (projectId: number, tab: string) => `files:${projectId}:${tab}`,
  
  // Gerar chave para PDF presign
  pdfPresign: (filePath: string) => `pdf:${btoa(filePath)}`,
  
  // Gerar chave para dados do usuário
  userData: (userId: number) => `user:${userId}`,
  
  // Gerar chave genérica
  generic: (namespace: string, ...parts: (string | number)[]) => 
    `${namespace}:${parts.join(':')}`,
} as const

